import ContentPreview from 'components/modules/ContentPreview/ContentPreview'
import { BubbleWrapper, LeftChatBubble, RightChatBubble, EmptyChatText, DateWrapper, EmptyKnowCard } from './ChatDisplay.css'
import isValidHttpUrl, { doesLinkIncludeAttachment } from 'lib/formatting/url'
import { DisplayChatMessage, UserChat } from 'interfaces/UserChat'
import KnowDisplayCard from 'components/modules/Knows/KnowDisplayCard'
import Stack from 'components/elements/Stack'
import _useTranslation from 'lib/hooks/_useTranslation'
import CardContainer from 'components/elements/CardContainer'
import { User } from 'interfaces/User'
import { showMessageTimeStamp } from 'lib/features/chat'
import ThreadMessages from '../UserChat/ThreadMessages'
import MessageReportDialog from '../../Reports/ReportDialog'
import { useState } from 'react'
import styled from 'styled-components'
import GroupMessageSender from '../UserChat/MessageSender'
import { ReportType } from 'interfaces/UserReport'

interface Props {
    chat?: UserChat
    chatMessages: DisplayChatMessage[]
    ownIdentification?: string
    groupMembers?: User[]
    fetchData?: () => void
    isDarkTheme?: boolean
    useMarkdown?: boolean
    isAiResponding?: boolean
}

const MessageRow = styled.div<{ isUser: boolean }>`
    display: flex;
    justify-content: ${({ isUser }) => (isUser ? 'end' : 'start')};
    align-items: center;

    p {
        width: fit-content;
        min-width: 30px;
    }
`

const ChatDisplay = ({ fetchData, chat, chatMessages, ownIdentification, groupMembers }: Props) => {
    const [messageIdToShowMenuOn, setMessageIdToShowMenuOn] = useState<number | null>(null)
    const { t } = _useTranslation('chat')

    const isUser = (chatMessage: DisplayChatMessage) => chatMessage.userIdentification === ownIdentification

    const textBasedOnMessage = (message: string, allowLinks: boolean) => {
        if (isValidHttpUrl(message)) {
            if (doesLinkIncludeAttachment(message)) {
                return <ContentPreview contentType="IMAGE" previewUrl={message} />
            } else if (allowLinks) {
                return (
                    <a href={message} target="_blank" rel="noreferrer">
                        {message}
                    </a>
                )
            } else {
                return message
            }
        }
        return message
    }

    const previewMessage = (chatMessage: DisplayChatMessage) => {
        if (chatMessage?.know) {
            return (
                <Stack gutter={5}>
                    {chatMessage.message ? <p>{chatMessage.message}</p> : null}
                    {chatMessage.know.uuid ? (
                        <KnowDisplayCard key={chatMessage.know.uuid} know={chatMessage.know} referrerScreen="chat" />
                    ) : (
                        <CardContainer heightFitContent hasStandardShadow>
                            <EmptyKnowCard>{t('chat/missingKnowText')}</EmptyKnowCard>
                        </CardContainer>
                    )}
                </Stack>
            )
        } else if (chatMessage?.attachmentUrl) {
            const isPDF = chatMessage.attachmentUrl.split('.').pop() === 'pdf'
            return (
                <Stack gutter={5}>
                    {chatMessage.message ? <p>{chatMessage.message}</p> : null}
                    <ContentPreview contentType={isPDF ? 'PDF' : 'IMAGE'} previewUrl={chatMessage.attachmentUrl} />
                </Stack>
            )
        } else if (chatMessage?.voiceMessagePath) {
            return <audio src={chatMessage.voiceMessagePath} controls />
        } else {
            return textBasedOnMessage(chatMessage.message, chatMessage.allowLinks)
        }
    }

    if (chatMessages.length === 0) {
        return <EmptyChatText>{t('chat/noMessagesFound')}</EmptyChatText>
    }

    return (
        <>
            {chatMessages.map((chatMessage, i, array) => (
                <BubbleWrapper
                    $isUser={isUser(chatMessage)}
                    key={chatMessage.uuid}
                    onMouseEnter={() => setMessageIdToShowMenuOn(i)}
                    onMouseLeave={() => setMessageIdToShowMenuOn(null)}
                >
                    <DateWrapper>
                        <GroupMessageSender
                            message={chatMessage}
                            currentIndex={i}
                            allMessages={array}
                            groupMembers={groupMembers}
                        />
                        {isUser(chatMessage) ? (
                            <MessageRow isUser>
                                {messageIdToShowMenuOn === i ? (
                                    <MessageReportDialog
                                        reportedUserUuid={chatMessage.userIdentification}
                                        reportType={ReportType.chatMessage}
                                        chatMessage={chatMessage}
                                    />
                                ) : null}
                                <RightChatBubble>
                                    {previewMessage(chatMessage)}
                                    {groupMembers?.length && chat ? (
                                        <ThreadMessages
                                            fetchData={fetchData}
                                            chat={chat}
                                            chatMessage={chatMessage}
                                            groupMembers={groupMembers}
                                        />
                                    ) : null}
                                </RightChatBubble>
                            </MessageRow>
                        ) : (
                            <MessageRow isUser={false}>
                                <LeftChatBubble>
                                    {previewMessage(chatMessage)}
                                    {groupMembers?.length && chat ? (
                                        <ThreadMessages
                                            fetchData={fetchData}
                                            chat={chat}
                                            chatMessage={chatMessage}
                                            groupMembers={groupMembers}
                                        />
                                    ) : null}
                                </LeftChatBubble>
                                {messageIdToShowMenuOn === i ? (
                                    <MessageReportDialog
                                        reportedUserUuid={chatMessage.userIdentification}
                                        reportType={ReportType.chatMessage}
                                        chatMessage={chatMessage}
                                    />
                                ) : null}
                            </MessageRow>
                        )}

                        {showMessageTimeStamp(chatMessage, i, array)}
                    </DateWrapper>
                </BubbleWrapper>
            ))}
        </>
    )
}

export default ChatDisplay
